import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Select from '../../common/Select';
import Input from '../../common/Input';
import Button from '../../common/Button';
import engineerService from '../../../services/engineerService';
import { useAuth } from '../../../hooks/useAuth';
import { ROLES } from '../../../constants/roles';

const AssignTicketModal = ({ isOpen, onClose, onSubmit, ticket }) => {
  const { user } = useAuth();
  const [engineers, setEngineers] = useState([]);
  const [selectedEngineer, setSelectedEngineer] = useState('');
  const [timeToAttend, setTimeToAttend] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadEngineers();
    }
  }, [isOpen, user?.id]);

  const loadEngineers = async () => {
    setLoading(true);
    // Only filter by TL if the logged in user is a Team Lead
    const teamLeadId = user?.role === ROLES.TL ? user.id : null;
    const result = await engineerService.getAllEngineers(teamLeadId);
    if (result.success) {
      // Filter only available/active/busy engineers
      const availableEngineers = result.data.filter(e => 
        e.is_active && ['Available', 'Active', 'Busy'].includes(e.status)
      );
      setEngineers(availableEngineers);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedEngineer) {
      setError('Please select an engineer');
      return;
    }

    if (!timeToAttend) {
      setError('Please provide time to attend');
      return;
    }

    const parsedTime = new Date(timeToAttend);
    if (Number.isNaN(parsedTime.getTime())) {
      setError('Please provide a valid time to attend');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(ticket.id, parseInt(selectedEngineer, 10), parsedTime.toISOString());
      setSelectedEngineer('');
      setTimeToAttend('');
      setError('');
      onClose();
    } catch (error) {
      console.error('Error assigning ticket:', error);
      setError('Failed to assign ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSelectedEngineer('');
      setTimeToAttend('');
      setError('');
      onClose();
    }
  };

  const engineerOptions = engineers.map(eng => ({
    value: eng.id.toString(),
    label: `${eng.name} (${eng.active_tickets || 0} active tickets)`
  }));

  const ticketInfoStyle = {
    background: 'var(--surface-light)',
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '1px solid var(--border)'
  };

  const infoRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '14px'
  };

  const labelStyle = {
    color: 'var(--text-secondary)',
    fontWeight: '600'
  };

  const valueStyle = {
    color: 'var(--text-primary)',
    fontWeight: '500'
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting || loading}>
        {submitting ? 'Assigning...' : 'Assign Ticket'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Assign Ticket to Engineer"
      size="medium"
      footer={footer}
    >
      <div style={ticketInfoStyle}>
        <div style={infoRowStyle}>
          <span style={labelStyle}>Ticket ID:</span>
          <span style={valueStyle}>#{ticket?.id}</span>
        </div>
        <div style={infoRowStyle}>
          <span style={labelStyle}>Title:</span>
          <span style={valueStyle}>{ticket?.title}</span>
        </div>
        <div style={infoRowStyle}>
          <span style={labelStyle}>Priority:</span>
          <span style={valueStyle}>{ticket?.priority}</span>
        </div>
        <div style={infoRowStyle}>
          <span style={labelStyle}>Site:</span>
          <span style={valueStyle}>{ticket?.site_name}</span>
        </div>
        {ticket?.assignee_name && (
          <div style={infoRowStyle}>
            <span style={labelStyle}>Current Assignee:</span>
            <span style={valueStyle}>{ticket.assignee_name}</span>
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>
          Loading engineers...
        </div>
      ) : engineers.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px', 
          color: 'var(--text-secondary)',
          background: 'var(--surface-light)',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}></div>
          <div>No available engineers found</div>
        </div>
      ) : (
        <>
          <Select
            label="Engineer"
            value={selectedEngineer}
            onChange={(e) => {
              setSelectedEngineer(e.target.value);
              setError('');
            }}
            options={engineerOptions}
            error={error}
            required
            placeholder="Choose an engineer..."
          />
          <Input
            label="Time to Attend"
            type="datetime-local"
            value={timeToAttend}
            onChange={(e) => {
              setTimeToAttend(e.target.value);
              setError('');
            }}
            required
          />
        </>
      )}

      {engineers.length > 0 && (
        <div style={{
          fontSize: '13px',
          color: 'var(--text-secondary)',
          marginTop: '16px',
          padding: '12px',
          background: 'var(--surface-light)',
          borderRadius: '8px',
          border: '1px solid var(--border)'
        }}>
           <strong>Tip:</strong> Engineers with fewer active tickets are shown first. Consider workload when assigning.
        </div>
      )}
    </Modal>
  );
};

export default AssignTicketModal;